import React, { useState } from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { useUserActions } from '../api/useUserActions';
import { RefundResponse, TransactionRedemption } from '../api/types';

const MIN_REASON_LEN = 20; // matches post-admin-redemption-refund

interface Props {
  env: Env;
  redemption: TransactionRedemption;
  onClose: () => void;
  onRefunded: (result: RefundResponse) => void;
}

/**
 * Redemption refund modal — credits the original cost back and flips the
 * redemption to `refunded`. Financial-impact action, so it takes a required
 * reason (>= 20 chars) and a confirm step that restates the amount, matching the
 * coin-adjust flow. The endpoint is idempotent, so a double-submit cannot
 * double-credit.
 */
export function RefundModal({ env, redemption, onClose, onRefunded }: Props) {
  const { loading, error, refundRedemption } = useUserActions(env);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const reasonValid = reason.trim().length >= MIN_REASON_LEN;
  const cost = redemption.coins_cost ?? 0;

  const submit = async () => {
    const res = await refundRedemption(redemption.id, reason.trim());
    if (res) {
      onRefunded(res);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-2xl p-5 shadow-xl shadow-black/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Refund redemption</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300" aria-label="Close">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-xs text-gray-400 mb-4">
          Redemption <span className="font-mono text-gray-300">{redemption.id.slice(0, 8)}…</span> ·{' '}
          <span className="text-white font-semibold">{cost.toLocaleString()}</span> coins ·{' '}
          {redemption.created_at ? new Date(redemption.created_at).toLocaleDateString() : 'unknown date'}
        </p>

        {error && (
          <div className="mb-3">
            <ErrorBanner message={error} />
          </div>
        )}

        {!confirming ? (
          <>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Reason ({reason.trim().length}/{MIN_REASON_LEN} min)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Why is this redemption being clawed back? Be specific — this is on the audit record."
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
            />

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:text-white">
                Cancel
              </button>
              <button
                onClick={() => setConfirming(true)}
                disabled={!reasonValid || loading}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Review
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-3 bg-gray-950 border border-gray-800 rounded-xl text-sm text-gray-300 mb-4">
              <p>
                Credit <span className="text-emerald-300 font-semibold">{cost.toLocaleString()}</span> coins back and
                mark the redemption <span className="text-white font-semibold">refunded</span>.
              </p>
              <p className="mt-2 text-xs text-gray-500">{reason.trim()}</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirming(false)}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white"
                disabled={loading}
              >
                Back
              </button>
              <button
                onClick={submit}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40 flex items-center gap-2"
              >
                {loading && <Spinner size="sm" />}
                Confirm refund
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
